import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Instagram, Mail } from 'lucide-react';
import {
  BRAND,
  REFERRAL_SOURCES,
  DURATIONS,
  BOOKING_TYPE_OPTIONS,
  BOOKING_CITIES,
  IMAGES,
} from '../lib/content';
import VerifyModal from '../components/VerifyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: '',
  age: '',
  email: '',
  phone: '',
  city: '',
  location_type: '',
  referral_source: '',
  preferred_date: '',
  start_time: '',
  duration: '',
  booking_type: '',
  desires: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[\d\s\+\-\(\)]{8,25}$/;

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  // Pre-fill city + desires context if arriving from Tour page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cityParam = params.get('city');
    if (cityParam) {
      setForm((f) => ({
        ...f,
        city: BOOKING_CITIES.includes(cityParam) ? cityParam : f.city,
        desires:
          f.desires ||
          `I saw you'll be in ${cityParam} \u2014 I'd love to meet while you're here.\n\n`,
      }));
    }
  }, [location.search]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please share your name.';
    const age = parseInt(form.age, 10);
    if (!form.age.toString().trim() || Number.isNaN(age)) return 'Please share your age.';
    if (age < 18) return 'You must be 18 or over to book.';
    if (age > 100) return 'Please enter a real age.';
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!email) return 'Please provide an email address.';
    if (!EMAIL_RE.test(email)) return 'That email doesn\u2019t look quite right. Please check the spelling.';
    if (!phone) return 'Please provide a phone number.';
    if (!PHONE_RE.test(phone) || phone.replace(/\D/g, '').length < 8) {
      return 'That phone number doesn\u2019t look quite right. Please include your country code (e.g. +61).';
    }
    if (!form.city) return 'Please choose a city.';
    if (!form.location_type) return 'Please choose incall or outcall.';
    if (!form.referral_source) return 'Please let me know where you found me.';
    if (!form.preferred_date.trim()) return 'Please share a preferred date.';
    if (!form.start_time.trim()) return 'Please suggest a start time (9am\u20139pm).';
    if (!form.duration) return 'Please choose a duration.';
    if (!form.booking_type) return 'Please choose a type of booking.';
    if (!form.desires.trim()) return 'Please share a little about what you\u2019re yearning for.';
    return null;
  };

  const requestCode = async () => {
    await axios.post(`${API}/verify/send`, {
      email: form.email.trim().toLowerCase(),
      first_name: form.name.trim() || null,
      purpose: 'booking',
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await requestCode();
      setVerifyError(null);
      setVerifyOpen(true);
    } catch (er) {
      const msg = er?.response?.data?.detail || 'Could not send verification code. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifySubmit = async (code) => {
    setVerifyError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, verification_code: code });
      setVerifyOpen(false);
      setSubmitted(true);
      toast.success('Your inquiry is with me. I\u2019ll be in touch soon.');
      setForm(initial);
    } catch (er) {
      const msg = er?.response?.data?.detail || 'That code didn\u2019t work. Please try again.';
      setVerifyError(typeof msg === 'string' ? msg : 'That code didn\u2019t work.');
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyResend = async () => {
    try {
      await requestCode();
      setVerifyError(null);
      toast.success('A new code is on its way.');
    } catch (er) {
      const msg = er?.response?.data?.detail || 'Could not send another code just yet.';
      setVerifyError(typeof msg === 'string' ? msg : 'Could not resend.');
    }
  };

  return (
    <div>
      <Toaster position="top-center" richColors closeButton />

      <VerifyModal
        open={verifyOpen}
        email={form.email.trim().toLowerCase()}
        firstName={form.name.trim() || null}
        purpose="booking"
        submitting={submitting}
        error={verifyError}
        onCancel={() => setVerifyOpen(false)}
        onSubmit={onVerifySubmit}
        onResend={onVerifyResend}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <div className="font-label text-zb-accent mb-6">Inquire</div>
        <h1 className="font-serif text-5xl md:text-7xl text-zb-ink tracking-tight leading-[0.95]">
          Let&rsquo;s <span className="italic text-zb-brand">begin.</span>
        </h1>
        <p className="mt-8 max-w-xl text-zb-ink2 text-lg leading-relaxed">
          Your move. I promise I don&rsquo;t bite (unless you ask nicely).
          I reply personally, usually within 24 hours.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="border border-zb-border bg-zb-bg2 p-10 md:p-14 zb-fade-up"
            >
              <div className="font-label text-zb-accent mb-4">Thank you</div>
              <h2 className="font-serif text-4xl md:text-5xl text-zb-ink leading-tight">
                Your note is <span className="italic text-zb-brand">with me.</span>
              </h2>
              <p className="mt-6 text-zb-ink2 text-base leading-relaxed max-w-md">
                I&rsquo;ll reply personally, usually within 24 hours. Keep an eye
                on your inbox (and your spam folder, just in case).
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 rounded-full border border-zb-border hover:border-zb-brand text-zb-ink hover:text-zb-brand px-6 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="booking-form"
              noValidate
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
            >
              <div className="md:col-span-2">
                <label className="font-label text-zb-accent block mb-1">Your name</label>
                <input
                  data-testid="booking-input-name"
                  className="zb-input"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="The name you&rsquo;d like me to use"
                />
              </div>

              <div>
                <label className="font-label text-zb-accent block mb-1">Age</label>
                <input
                  data-testid="booking-input-age"
                  type="number"
                  inputMode="numeric"
                  min="18"
                  max="100"
                  className="zb-input"
                  value={form.age}
                  onChange={update('age')}
                  placeholder="Your age (18+)"
                />
              </div>
              <div>
                <label className="font-label text-zb-accent block mb-1">City</label>
                <select
                  data-testid="booking-input-city"
                  className="zb-input"
                  value={form.city}
                  onChange={update('city')}
                >
                  <option value="">Select...</option>
                  {BOOKING_CITIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-label text-zb-accent block mb-1">Email</label>
                <input
                  data-testid="booking-input-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="zb-input"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@somewhere.com"
                />
              </div>
              <div>
                <label className="font-label text-zb-accent block mb-1">Phone</label>
                <input
                  data-testid="booking-input-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className="zb-input"
                  value={form.phone}
                  onChange={update('phone')}
                  placeholder="+61 ___ ___ ___"
                />
              </div>
              <p className="md:col-span-2 -mt-3 text-[12px] text-zb-accent italic">
                Both are required. I&rsquo;ll send a quick verification code to
                your email before submitting.
              </p>

              <div className="md:col-span-2">
                <label className="font-label text-zb-accent block mb-2">
                  Incall or outcall?
                </label>
                <div className="flex flex-wrap gap-3" data-testid="booking-input-location-type">
                  {['Incall', 'Outcall'].map((opt) => (
                    <label
                      key={opt}
                      className={`cursor-pointer inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm transition-colors ${
                        form.location_type === opt
                          ? 'border-zb-brand bg-zb-brand text-zb-bg'
                          : 'border-zb-border text-zb-ink hover:border-zb-brand'
                      }`}
                    >
                      <input
                        type="radio"
                        name="location_type"
                        value={opt}
                        checked={form.location_type === opt}
                        onChange={update('location_type')}
                        className="sr-only"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="font-label text-zb-accent block mb-1">
                  Where did you find me?
                </label>
                <select
                  data-testid="booking-input-referral"
                  className="zb-input"
                  value={form.referral_source}
                  onChange={update('referral_source')}
                >
                  <option value="">Select...</option>
                  {REFERRAL_SOURCES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-label text-zb-accent block mb-1">Preferred date</label>
                <input
                  data-testid="booking-input-date"
                  type="text"
                  className="zb-input"
                  value={form.preferred_date}
                  onChange={update('preferred_date')}
                  placeholder="e.g. Saturday 22 Feb"
                />
              </div>
              <div>
                <label className="font-label text-zb-accent block mb-1">
                  Preferred start time
                </label>
                <input
                  data-testid="booking-input-start-time"
                  type="text"
                  className="zb-input"
                  value={form.start_time}
                  onChange={update('start_time')}
                  placeholder="e.g. 2pm · between 9am – 9pm"
                />
                <p className="mt-1.5 text-[12px] text-zb-accent italic">
                  Between 9am – 9pm (I don&rsquo;t work late nights, unless it&rsquo;s a sleepover).
                </p>
              </div>

              <div>
                <label className="font-label text-zb-accent block mb-1">Duration</label>
                <select
                  data-testid="booking-input-duration"
                  className="zb-input"
                  value={form.duration}
                  onChange={update('duration')}
                >
                  <option value="">Select...</option>
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-label text-zb-accent block mb-1">Type of booking</label>
                <select
                  data-testid="booking-input-type"
                  className="zb-input"
                  value={form.booking_type}
                  onChange={update('booking_type')}
                >
                  <option value="">Select...</option>
                  {BOOKING_TYPE_OPTIONS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="font-label text-zb-accent block mb-1">
                  Tell me what you&rsquo;re yearning for
                </label>
                <textarea
                  data-testid="booking-input-desires"
                  className="zb-input"
                  rows={5}
                  value={form.desires}
                  onChange={update('desires')}
                  placeholder="Your desires, any fantasies you&rsquo;ve been turning over, and anything important I should know."
                />
              </div>

              <div className="md:col-span-2 pt-4 flex flex-wrap items-center gap-5">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-button"
                  className="rounded-full bg-zb-brand hover:bg-zb-brandHover disabled:opacity-60 text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
                >
                  {submitting ? 'Sending\u2026' : 'Send inquiry'}
                </button>
                <p className="text-[11px] text-zb-accent tracking-widest uppercase">
                  Discreet · Replies personally · 24h
                </p>
              </div>
            </form>
          )}
        </div>

        <aside className="md:col-span-5 md:pl-6">
          <div className="zb-img-wrap aspect-[4/5] mb-8">
            <img src={IMAGES.ratesAside} alt="" />
          </div>
          <div className="border-t border-zb-border pt-6">
            <div className="font-label text-zb-accent mb-3">Stay in touch</div>
            <a
              data-testid="contact-instagram"
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-zb-ink hover:text-zb-brand transition-colors"
            >
              <Instagram size={16} /> {BRAND.instagramHandle}
            </a>
            <div className="mt-3">
              <a
                data-testid="contact-email"
                href={`mailto:${BRAND.email}`}
                className="inline-flex items-center gap-2 text-zb-ink hover:text-zb-brand transition-colors"
              >
                <Mail size={16} /> {BRAND.email}
              </a>
            </div>
            <div className="mt-3 text-base text-zb-ink2">
              <span className="font-label text-zb-accent mr-2">SMS</span>
              <a
                href={`sms:${BRAND.phoneIntl.replace(/\s/g, '')}`}
                className="hover:text-zb-brand transition-colors"
                data-testid="contact-sms"
              >
                {BRAND.phoneIntl}
              </a>
            </div>
            <div className="mt-1.5 text-base text-zb-ink2">
              <span className="font-label text-zb-accent mr-2">WhatsApp</span>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="hover:text-zb-brand transition-colors"
                data-testid="contact-whatsapp"
              >
                Chat on WhatsApp →
              </a>
            </div>
          </div>
          <div className="mt-10 text-zb-ink2 text-base leading-relaxed">
            <div className="font-label text-zb-accent mb-3">First impressions</div>
            <p>
              Please include your name, desired date, location and type of
              booking. I believe in quality over quantity and reply personally
              to messages that come with care.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
